import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Send, MessageSquare, Sparkles, Copy, Download, Loader2, RefreshCw, User2, Briefcase, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const styleHelp = {
  classique: "Sobre et professionnel — pour grandes entreprises ou postes administratifs.",
  moderne: "Dynamique et clair — recommandé pour la plupart des candidatures.",
  creatif: "Original et personnalisé — pour communication, marketing, design.",
};

function CopyDownloadBar({ text, filename = "document.txt" }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={async () => {
          await navigator.clipboard.writeText(text);
          toast.success("Copié dans le presse-papier");
        }}
        data-testid="copy-btn"
      >
        <Copy size={13} className="mr-1.5" /> Copier
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = filename; a.click();
          URL.revokeObjectURL(url);
        }}
        data-testid="download-btn"
      >
        <Download size={13} className="mr-1.5" /> Télécharger
      </Button>
    </div>
  );
}

function CVGenerator({ onGenerated }) {
  const [form, setForm] = useState({
    nom_complet: "", email: "", telephone: "", ville: "",
    formation: "", experiences: "", competences: "",
    langues: "", centres_interet: "", poste_vise: "", style: "moderne",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult("");
    try {
      const { data } = await api.post("/ai/cv/generate", form);
      setResult(data.markdown);
      toast.success("CV généré ! Tu peux maintenant le copier ou le télécharger.");
      onGenerated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de génération");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="cv-form">
        <div>
          <Label htmlFor="nc">Nom complet *</Label>
          <Input id="nc" required value={form.nom_complet} onChange={(e) => update("nom_complet", e.target.value)} className="mt-1.5" data-testid="cv-name-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="em">Email *</Label>
            <Input id="em" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5" data-testid="cv-email-input" />
          </div>
          <div>
            <Label htmlFor="tel">Téléphone</Label>
            <Input id="tel" value={form.telephone} onChange={(e) => update("telephone", e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="vi">Ville</Label>
            <Input id="vi" value={form.ville} onChange={(e) => update("ville", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="pv">Poste visé</Label>
            <Input id="pv" placeholder="ex: Stage marketing, Job d'été" value={form.poste_vise} onChange={(e) => update("poste_vise", e.target.value)} className="mt-1.5" data-testid="cv-target-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="fo">Formation actuelle / récente *</Label>
          <Textarea id="fo" required value={form.formation} onChange={(e) => update("formation", e.target.value)} className="mt-1.5" rows={3} placeholder="ex: BUT Techniques de Commercialisation, IUT de Bayonne, 2024-2026" data-testid="cv-formation-input" />
        </div>
        <div>
          <Label htmlFor="ex">Expériences (jobs, stages, asso...)</Label>
          <Textarea id="ex" value={form.experiences} onChange={(e) => update("experiences", e.target.value)} className="mt-1.5" rows={3} placeholder="ex: Été 2025 — Vendeuse Décathlon (1 mois)..." />
        </div>
        <div>
          <Label htmlFor="co">Compétences</Label>
          <Textarea id="co" value={form.competences} onChange={(e) => update("competences", e.target.value)} className="mt-1.5" rows={2} placeholder="ex: Pack Office, Canva, sens du contact" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="la">Langues</Label>
            <Input id="la" value={form.langues} onChange={(e) => update("langues", e.target.value)} className="mt-1.5" placeholder="Français, Anglais B2, Espagnol A2" />
          </div>
          <div>
            <Label htmlFor="ci">Centres d'intérêt</Label>
            <Input id="ci" value={form.centres_interet} onChange={(e) => update("centres_interet", e.target.value)} className="mt-1.5" placeholder="Sport, lecture, voyages" />
          </div>
        </div>
        <div>
          <Label>Style</Label>
          <Select value={form.style} onValueChange={(v) => update("style", v)}>
            <SelectTrigger className="mt-1.5" data-testid="cv-style-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="moderne">Moderne</SelectItem>
              <SelectItem value="classique">Classique</SelectItem>
              <SelectItem value="creatif">Créatif</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">{styleHelp[form.style]}</p>
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-indigo-500 hover:bg-indigo-600" data-testid="cv-submit-btn">
          {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Génération…</> : <><Sparkles size={14} className="mr-1.5" /> Générer mon CV</>}
        </Button>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 min-h-[400px]" data-testid="cv-result">
        {!result && !busy && (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-20">
            <FileText size={36} className="mb-3" />
            <p className="text-sm">Ton CV apparaîtra ici une fois généré.</p>
          </div>
        )}
        {busy && (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-20">
            <Loader2 size={36} className="mb-3 animate-spin text-indigo-500" />
            <p className="text-sm">L'IA rédige ton CV…</p>
          </div>
        )}
        {result && (
          <div className="space-y-4">
            <CopyDownloadBar text={result} filename="cv-student-hub.md" />
            <div className="prose prose-slate max-w-none text-sm bg-white border border-slate-200 rounded-xl p-5 whitespace-pre-wrap font-mono leading-relaxed" data-testid="cv-output">
              {result}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LetterGenerator({ onGenerated }) {
  const [form, setForm] = useState({
    nom_complet: "", poste: "", entreprise: "",
    offre_description: "", experience_resume: "", motivation_perso: "", langue: "fr",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult("");
    try {
      const { data } = await api.post("/ai/cover-letter/generate", form);
      setResult(data.text);
      toast.success("Lettre générée !");
      onGenerated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de génération");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="letter-form">
        <div>
          <Label>Nom complet *</Label>
          <Input required value={form.nom_complet} onChange={(e) => update("nom_complet", e.target.value)} className="mt-1.5" data-testid="letter-name-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Poste visé *</Label>
            <Input required value={form.poste} onChange={(e) => update("poste", e.target.value)} className="mt-1.5" placeholder="ex: Stage marketing" data-testid="letter-poste-input" />
          </div>
          <div>
            <Label>Entreprise *</Label>
            <Input required value={form.entreprise} onChange={(e) => update("entreprise", e.target.value)} className="mt-1.5" placeholder="ex: Décathlon" data-testid="letter-company-input" />
          </div>
        </div>
        <div>
          <Label>Description de l'offre (optionnel)</Label>
          <Textarea value={form.offre_description} onChange={(e) => update("offre_description", e.target.value)} className="mt-1.5" rows={4} placeholder="Colle l'annonce de l'offre pour personnaliser au maximum…" />
        </div>
        <div>
          <Label>Mon expérience en bref</Label>
          <Textarea value={form.experience_resume} onChange={(e) => update("experience_resume", e.target.value)} className="mt-1.5" rows={3} placeholder="ex: Étudiant en BUT TC 2ᵉ année, stage de 2 mois en boutique." />
        </div>
        <div>
          <Label>Pourquoi cette entreprise ? (optionnel)</Label>
          <Textarea value={form.motivation_perso} onChange={(e) => update("motivation_perso", e.target.value)} className="mt-1.5" rows={2} placeholder="ex: J'admire leur engagement écologique." />
        </div>
        <div>
          <Label>Langue</Label>
          <Select value={form.langue} onValueChange={(v) => update("langue", v)}>
            <SelectTrigger className="mt-1.5" data-testid="letter-lang-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">Anglais</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-indigo-500 hover:bg-indigo-600" data-testid="letter-submit-btn">
          {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Génération…</> : <><Sparkles size={14} className="mr-1.5" /> Générer ma lettre</>}
        </Button>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 min-h-[400px]" data-testid="letter-result">
        {!result && !busy && (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-20">
            <Send size={36} className="mb-3" />
            <p className="text-sm">Ta lettre apparaîtra ici.</p>
          </div>
        )}
        {busy && (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-20">
            <Loader2 size={36} className="mb-3 animate-spin text-indigo-500" />
            <p className="text-sm">L'IA rédige ta lettre…</p>
          </div>
        )}
        {result && (
          <div className="space-y-4">
            <CopyDownloadBar text={result} filename="lettre-motivation.txt" />
            <div className="text-sm bg-white border border-slate-200 rounded-xl p-5 whitespace-pre-wrap leading-relaxed" data-testid="letter-output">
              {result}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InterviewSimulator({ onGenerated }) {
  const [phase, setPhase] = useState("setup"); // setup | running
  const [setup, setSetup] = useState({ type_poste: "", secteur: "", experience: "debutant" });
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/ai/interview/start", setup);
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", text: data.message }]);
      setPhase("running");
      onGenerated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Démarrage impossible");
    } finally {
      setBusy(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userText = input.trim();
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/interview/message", { session_id: sessionId, message: userText });
      setMessages((m) => [...m, { role: "assistant", text: data.message }]);
    } catch (err) {
      toast.error("Erreur. Réessaie.");
      setMessages((m) => [...m, { role: "assistant", text: "_(Erreur — réessaie ta réponse)_" }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPhase("setup");
    setSessionId("");
    setMessages([]);
    setInput("");
  };

  if (phase === "setup") {
    return (
      <form onSubmit={start} className="max-w-xl space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="interview-setup">
        <p className="font-display font-semibold text-slate-900">Démarre ton entretien simulé</p>
        <p className="text-sm text-slate-500">5 questions adaptées + feedback à chaque réponse + bilan final.</p>
        <div>
          <Label>Type de poste *</Label>
          <Input required value={setup.type_poste} onChange={(e) => setSetup({ ...setup, type_poste: e.target.value })} className="mt-1.5" placeholder="ex: Stage assistant marketing, Job de serveur, Alternance dev" data-testid="interview-poste-input" />
        </div>
        <div>
          <Label>Secteur (optionnel)</Label>
          <Input value={setup.secteur} onChange={(e) => setSetup({ ...setup, secteur: e.target.value })} className="mt-1.5" placeholder="ex: Distribution, Tech, Restauration" />
        </div>
        <div>
          <Label>Niveau d'expérience</Label>
          <Select value={setup.experience} onValueChange={(v) => setSetup({ ...setup, experience: v })}>
            <SelectTrigger className="mt-1.5" data-testid="interview-level-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="debutant">Débutant (premier job)</SelectItem>
              <SelectItem value="intermediaire">Intermédiaire (1-2 expériences)</SelectItem>
              <SelectItem value="confirme">Confirmé (alternance, longue expérience)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-indigo-500 hover:bg-indigo-600" data-testid="interview-start-btn">
          {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Démarrage…</> : <><MessageSquare size={14} className="mr-1.5" /> Lancer l'entretien</>}
        </Button>
      </form>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="interview-running">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Entretien : <strong className="text-slate-900">{setup.type_poste}</strong></p>
        <Button size="sm" variant="outline" onClick={reset} data-testid="interview-reset-btn">
          <RefreshCw size={13} className="mr-1.5" /> Recommencer
        </Button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6 space-y-4 min-h-[400px] max-h-[600px] overflow-y-auto" data-testid="interview-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-indigo-500 text-white" : "bg-slate-900 text-white"}`}>
              {m.role === "user" ? <User2 size={14} /> : <Briefcase size={14} />}
            </div>
            <div className={`rounded-2xl p-3.5 text-sm whitespace-pre-wrap leading-relaxed max-w-[80%] ${m.role === "user" ? "bg-indigo-500 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-slate-900 text-white">
              <Briefcase size={14} />
            </div>
            <div className="rounded-2xl p-3.5 bg-white border border-slate-200">
              <Loader2 size={14} className="animate-spin text-indigo-500" />
            </div>
          </div>
        )}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={2}
          placeholder="Ta réponse à la question…"
          disabled={busy}
          data-testid="interview-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(e);
            }
          }}
        />
        <Button type="submit" disabled={busy || !input.trim()} className="bg-indigo-500 hover:bg-indigo-600 self-stretch" data-testid="interview-send-btn">
          <Send size={14} />
        </Button>
      </form>
    </div>
  );
}

export default function AIToolsJobs() {
  const [quota, setQuota] = useState(null);

  const refreshQuota = async () => {
    try {
      const { data } = await api.get("/ai/quota");
      setQuota(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { refreshQuota(); }, []);

  const exhausted = quota && !quota.unlimited && quota.used >= quota.limit;

  return (
    <div className="space-y-8" data-testid="ai-jobs-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Outils IA — Jobs</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Décroche ton job 10x plus vite 🚀</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          3 outils IA pour optimiser ta candidature : génère un CV adapté, écris une lettre percutante, prépare-toi à l'entretien.
        </p>
      </header>

      {/* Quota badge */}
      {quota && (
        <div
          className={`rounded-2xl p-5 flex items-center gap-4 ${
            quota.unlimited
              ? "bg-gradient-to-br from-amber-50 to-yellow-100 border border-yellow-200"
              : exhausted
              ? "bg-red-50 border border-red-200"
              : "bg-indigo-50 border border-indigo-100"
          }`}
          data-testid="ai-quota-card"
        >
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
            quota.unlimited ? "bg-yellow-500 text-white" : exhausted ? "bg-red-500 text-white" : "bg-indigo-500 text-white"
          }`}>
            {quota.unlimited ? <Crown size={20} /> : exhausted ? <Lock size={20} /> : <Sparkles size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            {quota.unlimited ? (
              <>
                <p className="font-display font-semibold text-slate-900">Tu es Premium ✓</p>
                <p className="text-sm text-slate-600">Générations IA <strong>illimitées</strong> ce mois-ci.</p>
              </>
            ) : exhausted ? (
              <>
                <p className="font-display font-semibold text-red-900">Quota gratuit atteint</p>
                <p className="text-sm text-red-800">Tu as utilisé {quota.used}/{quota.limit} générations ce mois-ci. Passe Premium pour un accès illimité (2,99 €/mois).</p>
              </>
            ) : (
              <>
                <p className="font-display font-semibold text-slate-900" data-testid="ai-quota-counter">
                  {quota.limit - quota.used} génération{quota.limit - quota.used > 1 ? "s" : ""} gratuite{quota.limit - quota.used > 1 ? "s" : ""} restante{quota.limit - quota.used > 1 ? "s" : ""} ce mois
                </p>
                <p className="text-sm text-slate-600">{quota.used}/{quota.limit} utilisées · Premium = illimité.</p>
              </>
            )}
          </div>
          {!quota.unlimited && (
            <Link
              to="/premium"
              className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 whitespace-nowrap"
              data-testid="ai-quota-upgrade-cta"
            >
              <Crown size={13} className="inline mr-1.5" /> Passer Premium
            </Link>
          )}
        </div>
      )}

      <Tabs defaultValue="cv" className="w-full">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="cv" data-testid="ai-tab-cv"><FileText size={14} className="mr-1.5" /> CV étudiant</TabsTrigger>
          <TabsTrigger value="letter" data-testid="ai-tab-letter"><Send size={14} className="mr-1.5" /> Lettre de motivation</TabsTrigger>
          <TabsTrigger value="interview" data-testid="ai-tab-interview"><MessageSquare size={14} className="mr-1.5" /> Simu entretien</TabsTrigger>
        </TabsList>
        <TabsContent value="cv" className="pt-6"><CVGenerator onGenerated={refreshQuota} /></TabsContent>
        <TabsContent value="letter" className="pt-6"><LetterGenerator onGenerated={refreshQuota} /></TabsContent>
        <TabsContent value="interview" className="pt-6"><InterviewSimulator onGenerated={refreshQuota} /></TabsContent>
      </Tabs>
    </div>
  );
}
